import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Badge from "../common/Badge";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import Skeleton from "../common/Skeleton";
import { getAiConfigs, getClients } from "../../services/aiConfigApi";
import {
  createPhoneNumber,
  getPhoneNumbers,
  updatePhoneNumber,
} from "../../services/phoneNumbersApi";
import { normalizeApiError } from "../../utils/errors";

const EMPTY_FORM = {
  phone_number_id: "",
  number_e164: "",
  client_id: "",
  ai_config_id: "",
  trunk_name: "",
  provider: "yeastar",
  direction: "inbound",
  status: "active",
};

function buildErrors() {
  return {
    number_e164: "",
  };
}

function validateForm(form) {
  const errors = buildErrors();
  if (!String(form.number_e164 || "").trim()) {
    errors.number_e164 = "Phone number is required.";
  }
  return errors;
}

export default function PhoneNumberRoutingManager({ showToast }) {
  const mountedRef = useRef(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [clients, setClients] = useState([]);
  const [aiConfigs, setAiConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState(buildErrors());

  const clientNameById = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          String(client.client_id),
          client.name || `Client ${client.client_id}`,
        ]),
      ),
    [clients],
  );

  const aiConfigById = useMemo(
    () =>
      new Map(
        aiConfigs.map((config) => [
          String(config.id),
          config,
        ]),
      ),
    [aiConfigs]
  );

  const activeAiConfigs = useMemo(
    () => aiConfigs.filter((config) => config && config.status === "ACTIVE"),
    [aiConfigs]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [phoneNumbersResult, clientsResult, aiConfigsResult] = await Promise.all([
        getPhoneNumbers({ page: 1, limit: 200 }),
        getClients(),
        getAiConfigs({ page: 1, limit: 200 }),
      ]);

      if (!mountedRef.current) {
        return;
      }

      setPhoneNumbers(phoneNumbersResult.items);
      setClients(clientsResult);
      setAiConfigs(Array.isArray(aiConfigsResult?.items) ? aiConfigsResult.items : []);
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort && mountedRef.current) {
        setError(normalizedError.message || "Unable to load phone number routing.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadData().catch(() => undefined);

    return () => {
      mountedRef.current = false;
    };
  }, [loadData]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFormErrors(buildErrors());
  };

  const handleEdit = (item) => {
    setForm({
      phone_number_id: String(item.phone_number_id || ""),
      number_e164: item.number_e164 || "",
      client_id: item.client_id ? String(item.client_id) : "",
      ai_config_id: item.ai_config_id ? String(item.ai_config_id) : "",
      trunk_name: item.trunk_name || "",
      provider: item.provider || "yeastar",
      direction: item.direction || "inbound",
      status: item.status || "active",
    });
    setFormErrors(buildErrors());
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);
    setFormErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        number_e164: form.number_e164.trim(),
        client_id: form.client_id ? Number(form.client_id) : null,
        ai_config_id: form.ai_config_id || null,
        trunk_name: form.trunk_name.trim() || null,
        provider: form.provider.trim() || null,
        direction: form.direction,
        status: form.status,
      };

      if (form.phone_number_id) {
        await updatePhoneNumber(form.phone_number_id, payload);
        showToast("Phone number routing updated successfully.", false, 4000);
      } else {
        await createPhoneNumber(payload);
        showToast("Phone number routing created successfully.", false, 4000);
      }

      resetForm();
      await loadData();
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  const handleClearClient = async (item) => {
    setBusyId(String(item.phone_number_id));
    try {
      await updatePhoneNumber(item.phone_number_id, {
        client_id: null,
        ai_config_id: null,
      });
      showToast("Phone number routing cleared.", false, 4000);
      await loadData();
    } catch (errorValue) {
      const normalizedError = normalizeApiError(errorValue);
      if (!normalizedError.isAbort) {
        showToast(normalizedError.message, true, 5000);
      }
    } finally {
      if (mountedRef.current) {
        setBusyId("");
      }
    }
  };

  return (
    <>
      <section className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div className="team-card-heading">Phone Number Routing</div>
          <div className="page-subtitle">
            Map inbound phone numbers to clients or one exact active AI runtime configuration
            so server 1 resolves the intended inbound behavior without code changes.
          </div>
        </div>

        <div className="settings-inline-help" style={{ marginBottom: "12px" }}>
          If you choose an <strong>AI runtime configuration</strong>, inbound calls to this number
          will use that exact active config first. If you leave it blank, resolution falls back
          to the client assignment and then the default active configuration.
        </div>

        <div className="settings-fields">
          <div className="settings-script-grid">
            <label className="settings-field">
              <span className="settings-label">Phone Number</span>
              <input
                className="settings-input"
                value={form.number_e164}
                onChange={(event) =>
                  setForm((current) => ({ ...current, number_e164: event.target.value }))
                }
                placeholder="+919444233942"
              />
              {formErrors.number_e164 ? (
                <span className="settings-inline-error">{formErrors.number_e164}</span>
              ) : null}
            </label>

            <label className="settings-field">
              <span className="settings-label">Client</span>
              <select
                className="settings-select"
                value={form.client_id}
                onChange={(event) =>
                  setForm((current) => {
                    const nextClientId = event.target.value;
                    const selectedConfig = current.ai_config_id
                      ? aiConfigById.get(String(current.ai_config_id))
                      : null;
                    const shouldClearConfig =
                      selectedConfig?.client_id &&
                      nextClientId &&
                      String(selectedConfig.client_id) !== String(nextClientId);

                    return {
                      ...current,
                      client_id: nextClientId,
                      ai_config_id: shouldClearConfig ? "" : current.ai_config_id,
                    };
                  })
                }
              >
                <option value="">Unassigned</option>
                {clients.map((client) => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Inbound AI Runtime Config</span>
              <select
                className="settings-select"
                value={form.ai_config_id}
                onChange={(event) =>
                  setForm((current) => {
                    const nextConfigId = event.target.value;
                    const selectedConfig = nextConfigId
                      ? aiConfigById.get(String(nextConfigId))
                      : null;

                    return {
                      ...current,
                      ai_config_id: nextConfigId,
                      client_id: selectedConfig?.client_id
                        ? String(selectedConfig.client_id)
                        : nextConfigId
                          ? ""
                          : current.client_id,
                    };
                  })
                }
              >
                <option value="">Use client active config / default fallback</option>
                {activeAiConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.company_name ? `${config.name} - ${config.company_name}` : config.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Trunk</span>
              <input
                className="settings-input"
                value={form.trunk_name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, trunk_name: event.target.value }))
                }
                placeholder="yeastar-trunk"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Direction</span>
              <select
                className="settings-select"
                value={form.direction}
                onChange={(event) =>
                  setForm((current) => ({ ...current, direction: event.target.value }))
                }
              >
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            </label>

            <label className="settings-field">
              <span className="settings-label">Provider</span>
              <input
                className="settings-input"
                value={form.provider}
                onChange={(event) =>
                  setForm((current) => ({ ...current, provider: event.target.value }))
                }
                placeholder="yeastar"
              />
            </label>

            <label className="settings-field">
              <span className="settings-label">Status</span>
              <select
                className="settings-select"
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({ ...current, status: event.target.value }))
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>

          <div className="page-actions">
            <Button variant="primary" disabled={saving} onClick={handleSubmit}>
              {saving ? "Saving..." : form.phone_number_id ? "Update Routing" : "Create Routing"}
            </Button>
            <Button disabled={saving} onClick={resetForm}>
              Clear
            </Button>
            <Button disabled={loading} onClick={() => loadData().catch(() => undefined)}>
              Refresh
            </Button>
          </div>
        </div>
      </section>

      <section className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div className="team-card-heading">Current Number Assignments</div>
          <div className="page-subtitle">
            These mappings decide which client profile is used for inbound calls when the
            number is recognized.
          </div>
        </div>

        {loading ? (
          <div className="empty-state">
            <Skeleton style={{ height: 16, width: 220, margin: "0 auto 8px" }} />
            <Skeleton style={{ height: 12, width: 160, margin: "0 auto" }} />
          </div>
        ) : error ? (
          <EmptyState title="Unable to load phone number routing" subtitle={error} />
        ) : !phoneNumbers.length ? (
          <EmptyState
            title="No phone numbers configured"
            subtitle="Add inbound numbers here so the AI runtime can resolve the correct client automatically."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Phone Number</th>
                  <th>Client</th>
                  <th>AI Config</th>
                  <th>Trunk</th>
                  <th>Direction</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {phoneNumbers.map((item) => {
                  const isBusy = busyId === String(item.phone_number_id);
                  return (
                    <tr key={item.phone_number_id}>
                      <td>{item.number_e164}</td>
                      <td>
                        {item.client_id
                          ? clientNameById.get(String(item.client_id)) || `Client ${item.client_id}`
                          : "Unassigned"}
                      </td>
                      <td>
                        {item.ai_config_id
                          ? aiConfigById.get(String(item.ai_config_id))?.name || item.ai_config_id
                          : "Client active config"}
                      </td>
                      <td>{item.trunk_name || "Not set"}</td>
                      <td>{item.direction || "inbound"}</td>
                      <td>
                        <Badge variant={item.status === "active" ? "blue" : "info"}>
                          {item.status || "active"}
                        </Badge>
                      </td>
                      <td className="td-actions">
                        <div className="settings-script-actions">
                          <Button disabled={isBusy} onClick={() => handleEdit(item)}>
                            Edit
                          </Button>
                          <Button
                            disabled={isBusy || (!item.client_id && !item.ai_config_id)}
                            onClick={() => handleClearClient(item)}
                          >
                            Clear Routing
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
